/**
 * Appearance preference — light by default, with dark or system available by choice.
 *
 * Three values, one of which is "no opinion": `system` leaves the document element without a
 * `data-theme`, so `packages/ui`'s `prefers-color-scheme` rules decide and keep deciding when the
 * device changes. `light`/`dark` set the attribute, which the tokens let win over the media query
 * in both directions.
 *
 * The choice is remembered on this device under its own key, like the recipient journey's locale:
 * it is a display preference, so it is never sent anywhere and never part of the vault.
 */

export const APPEARANCE_KEY = 'signbento.appearance'

export const APPEARANCES = ['system', 'light', 'dark'] as const

export type Appearance = (typeof APPEARANCES)[number]

/** The theme an appearance resolves to when it has one; `system` defers to the device. */
export function isAppearance(value: unknown): value is Appearance {
  return typeof value === 'string' && (APPEARANCES as readonly string[]).includes(value)
}

/** A fresh or unreadable preference starts in the light visual system used by the launch flows. */
export function readAppearance(storage: Pick<Storage, 'getItem'> | null): Appearance {
  if (storage === null) return 'light'
  try {
    const stored = storage.getItem(APPEARANCE_KEY)
    return isAppearance(stored) ? stored : 'light'
  } catch {
    // A blocked storage still gets a usable, predictable first-paint theme.
    return 'light'
  }
}

export function rememberAppearance(
  storage: Pick<Storage, 'setItem'> | null,
  appearance: Appearance,
): void {
  if (storage === null) return
  try {
    storage.setItem(APPEARANCE_KEY, appearance)
  } catch {
    // Remembering is a convenience; losing it must never break the app.
  }
}

/**
 * The store the preference lives in — `null` where there is no usable one (a host without a
 * `window`, or a storage the browser refuses to hand over), so the callers have one shape to test.
 */
export function appearanceStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

/** The element the attribute goes on — the document element, so every subtree follows. */
export interface ThemedElement {
  readonly dataset: { theme?: string }
}

/**
 * Applies an appearance: `system` removes the attribute (the media query decides), and an explicit
 * choice sets it. Nothing else is touched, so a person switching to `system` really does go back to
 * following the device rather than to a frozen copy of what it was.
 */
export function applyAppearance(element: ThemedElement, appearance: Appearance): void {
  if (appearance === 'system') {
    delete element.dataset.theme
    return
  }
  element.dataset.theme = appearance
}
